"use client";

// useHSwipe — the one horizontal-swipe model for the whole shell. Drag left/right
// anywhere on the page to move between siblings (folder levels) or documents
// (page levels). The hard part is arbitration: it must NEVER hijack vertical
// scroll, a horizontally-scrollable region (code block, the chip strip), a
// folder being dragged, or an explicit no-swipe control. So:
//   • axis-locked to x (use-gesture only fires the swipe once intent is horizontal)
//   • the surface keeps `touch-action: pan-y`, so the browser owns vertical
//   • on first touch we bail if the target sits in [data-noswipe], a form control,
//     an open horizontally-scrollable region that can still scroll that way, or a
//     folder ([data-folder]) which claims its own drag.
// Listeners are passive so native scrolling stays smooth.

import { useRef } from "react";
import { useDrag } from "@use-gesture/react";

interface Options {
  onPrev: () => void;
  onNext: () => void;
  enabled?: boolean;
  threshold?: number;
}

// Only a box that LETS the finger scroll it sideways counts: an author-marked
// strip (data-hscroll), or one whose computed overflow-x is auto/scroll. A box
// that merely has a wide child (overflow hidden/clip — the stage's own scroller,
// the page's column) never scrolls sideways, so it must never block the swipe.
function scrollsX(el: HTMLElement): boolean {
  if (el.dataset?.hscroll !== undefined) return true;
  if (el.scrollWidth <= el.clientWidth + 2) return false;
  const ox = getComputedStyle(el).overflowX;
  return ox === "auto" || ox === "scroll";
}

function canScrollX(el: HTMLElement | null, dir: number): boolean {
  let cur: HTMLElement | null = el;
  while (cur && cur !== document.body) {
    if (scrollsX(cur)) {
      const atStart = cur.scrollLeft <= 0;
      const atEnd = cur.scrollLeft + cur.clientWidth >= cur.scrollWidth - 1;
      // dir > 0 means the finger moves right (content would scroll left / show prev)
      if (dir > 0 && !atStart) return true;
      if (dir < 0 && !atEnd) return true;
    }
    cur = cur.parentElement;
  }
  return false;
}

// NO STATE. This used to hold the live drag offset in React state and set it on
// every drag frame — and its owner is the whole dashboard shell, so a sideways
// drag re-rendered the entire dashboard once per pointer event (hundreds a
// second on a fast mouse). Nothing read the offset (the navigator's navOffset
// was never used). The swipe only needs to know where it ENDED.
export function useHSwipe({ onPrev, onNext, enabled = true, threshold = 56 }: Options) {
  const ignore = useRef(false);

  const bind = useDrag(
    ({ event, first, last, movement: [mx], direction: [dx], active }) => {
      if (!enabled) return;
      if (first) {
        const t = event?.target as HTMLElement | null;
        const inControl = !!t?.closest?.("input,textarea,select,[data-noswipe],[data-folder]");
        ignore.current = inControl || canScrollX(t, mx >= 0 ? 1 : -1);
        if (ignore.current) return;
      }
      if (ignore.current) return;
      if (!active) {
        if (last && Math.abs(mx) > threshold) {
          if (dx > 0) onPrev();
          else onNext();
        }
      }
    },
    { axis: "x", filterTaps: true, pointer: { touch: true }, eventOptions: { passive: true } },
  );

  return { bind };
}

export default useHSwipe;
